import React, { useState } from "react";
import { Loader2, Printer, QrCode, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { Btn, Input, Select } from "../UI";
import { errMsg, downloadBlob, FORMATS, THEMES } from "./format";

export const scanUrl = (eid, staffId) => `${window.location.origin}/scan?src=${eid}${staffId ? `&ref=${staffId}` : ""}`;

export const ScanCardsForm = ({ ev, reload }) => {
  const [format, setFormat] = useState(ev.card_format || "business");
  const [theme, setTheme] = useState(ev.card_theme || "dark");
  const [staffId, setStaffId] = useState("");
  const [count, setCount] = useState(format === "letter" ? 1 : 10);
  const [busy, setBusy] = useState(false);
  const qrSrc = `${API}/public/scan/qr.png?src=${ev.id}${staffId ? `&ref=${staffId}` : ""}&size=200&origin=${encodeURIComponent(window.location.origin)}`;

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/promotions/events/${ev.id}/scan-cards`, { format, theme, staff_id: staffId || null, count: parseInt(count) || 1 }, { responseType: "blob" });
      downloadBlob(res.data, `find-your-race-${format}-${ev.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`);
      toast.success(`PDF ready — ${res.headers?.["x-card-count"] || count} card(s)`);
      reload();
    } catch (e) {
      const detail = e?.response?.data instanceof Blob ? await e.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return null; } }) : null;
      toast.error(detail || errMsg(e, "Could not generate the PDF"));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-[1fr_auto] gap-4" data-testid="scan-cards-form">
      <div className="space-y-3">
        <div className="text-xs text-slate-300">One tracked QR per trade show (and per staffer if you pick one). Visitors land on <span className="text-amber-300">/scan</span>, find their race and open a no-login demo — every scan, report and demo session is attributed here.</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Select label="Format" value={format} onChange={(e) => { setFormat(e.target.value); setCount(e.target.value === "letter" ? 1 : 10); }} data-testid="scan-format-select">
            {FORMATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
          <div>
            <div className="text-xs text-slate-400 mb-1">Look</div>
            <div className="flex gap-1.5" data-testid="scan-theme-toggle">
              {THEMES.map(([k, l]) => (
                <button key={k} onClick={() => setTheme(k)} data-testid={`scan-theme-${k}`}
                  className={`flex-1 text-xs px-3 py-2 rounded-lg border transition-colors ${theme === k ? "border-amber-400/60 bg-amber-400/10 text-amber-200" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>{l}</button>
              ))}
            </div>
          </div>
          <Select label="Attribute to" value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="scan-staff-select">
            <option value="">Whole trade show</option>
            {(ev.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <Input label={format === "letter" ? "Pages" : "Cards"} type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)} className="w-28" data-testid="scan-count-input" />
          <Btn onClick={generate} disabled={busy} data-testid="scan-generate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Generate PDF
          </Btn>
          <a href={qrSrc.replace("size=200", "size=1024")} download={`find-your-race-qr-${ev.id.slice(0, 8)}.png`} className="text-xs text-slate-400 hover:text-amber-300 inline-flex items-center gap-1" data-testid="scan-qr-download">
            <Download className="w-3.5 h-3.5" /> QR only (PNG)
          </a>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-paper p-3 self-start" data-testid="scan-qr-preview">
        <img src={qrSrc} alt="Find Your Race QR" width={140} height={140} />
        <div className="text-[10px] text-slate-600 inline-flex items-center gap-1 max-w-[160px] break-all text-center"><QrCode className="w-3 h-3 shrink-0" />{scanUrl(ev.id, staffId).replace(/^https?:\/\//, "")}</div>
      </div>
    </div>
  );
};
